<template>
	<div class="relative grow flex flex-col min-h-px">
		<div
			class="relative overflow-auto h-full"
			data-messenger-scrollwrap
			@scrollend="chatStore.handleLastReadMessage()"
		>
			<div class="container h-full py-3">
				<div class="h-px" data-chat-top-sentinel></div>

				<!-- sessions -->
				<section
					v-for="session in chatStore.currentChannel?.sessions"
					:key="session.id"
					:data-session-id="session.id"
				>
					<div class="py-3">
						<!-- Session date -->
						<InfoPill
							>{{
								getSessionDateDescription(session.created, session.closedDate)
							}}
						</InfoPill>
					</div>

					<template v-if="chatStore.isSessionLoaded(session.id)">
						<!-- Messages -->
						<TransitionGroup name="message">
							<div
								:class="['group py-3', getDataMessageType(message.authorId)]"
								v-for="message in session.messages"
								:key="message.messageIndex"
								:data-message-index="message.messageIndex"
							>
								<div
									v-if="chatStore.newMessagesMarker === message.messageIndex"
									class="relative mb-6 text-center text-gray-darker before:bg-gray before:w-full before:h-px before:absolute before:-z-1 before:start-0 before:top-1/2 before:-translate-y-1/2"
								>
									<!-- New unread messages -->
									<span class="bg-white inline-block px-5"> Nové zprávy </span>
								</div>
								<div v-if="message.isFirstOfDay" class="py-3">
									<!-- First message of the day -->
									<InfoPill>
										{{
											formatDate(message.created, {
												autoFormat: false,
												showDate: true,
												showTime: false,
												showWeekday: true
											})
										}}
									</InfoPill>
								</div>
								<div
									class="message-component group-[.received]:origin-bottom-left group-[.sent]:origin-bottom-right"
								>
									<component
										:is="getMessageType(message.messageType)"
										:message="message"
									></component>
								</div>
							</div>
						</TransitionGroup>
					</template>
					<template v-else>
						<!-- Messages Placeholders -->
						<div
							v-for="number in session.messageCount"
							:class="['group py-3', number % 2 === 0 ? 'sent' : 'received']"
							:key="number"
							:data-message-index="
								(session.lastMessageIndex || 0) - session.messageCount + number
							"
						>
							<MessagePlaceholder
								:type="number % 2 === 0 ? 'sent' : 'received'"
							></MessagePlaceholder>
						</div>
					</template>
				</section>

				<div class="h-px" data-chat-bottom-sentinel></div>
			</div>
		</div>

		<!-- Scroll Back Button -->
		<Transition name="fade-in-left">
			<ScrollBackButton v-show="!isAtBottom"></ScrollBackButton>
		</Transition>
	</div>
</template>

<script setup lang="ts">
import MessagePlaceholder from './MessagePlaceholder.vue'
import InfoPill from './InfoPill.vue'
import ScrollBackButton from '@/components/ScrollBackButton.vue'
import { useChatScroll } from '@/composables/useChatScroll'
const { isAtBottom } = useChatScroll()
import { useChatStore } from '@/stores/chatStore'
const chatStore = useChatStore()
import { formatDate, isSameDay } from '@/services/dateTimeService'
import { getMessageType } from '@/services/messages'
import type { MessageDeliveryState } from '@/types/Message'

const getSessionDateDescription = (
	sessionStart: string,
	sessionEnd: string | null
) => {
	const sessionStartDate = new Date(sessionStart)
	const currentDate = new Date()

	let formattedString = ''

	// start date
	formattedString += formatDate(sessionStart, {
		autoFormat: false,
		showWeekday: false,
		showTime: false,
		showDate: true,
		showYear: sessionStartDate.getFullYear() !== currentDate.getFullYear()
	})

	if (sessionEnd) {
		// end day exists
		const sessionEndDate = new Date(sessionEnd)

		if (!isSameDay(sessionStartDate, sessionEndDate)) {
			// end date is different than start date
			formattedString += ' - '

			formattedString += formatDate(sessionEnd, {
				autoFormat: false,
				showWeekday: false,
				showTime: false,
				showDate: true,
				showYear: sessionStartDate.getFullYear() !== currentDate.getFullYear()
			})
		}
	}

	return formattedString
}

const getDataMessageType = (authorId: number | null): MessageDeliveryState => {
	return chatStore.currentChannelMember?.id === authorId ? 'sent' : 'received'
}
</script>

<style scoped></style>
